import React, { useCallback, useEffect, useState } from "react";

import { CircularProgress, Stack } from "@mui/material";
import EmptyCardAssets from "assets/images/EmptyCardAssets.png";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { Chart, DispersionChart, InvestmentDetailActions, Table } from "components/Investment-detail";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useActionHook, useDevice } from "hooks";
import { useDealActions } from "store";
import {
  useDealIdSelector,
  useDealInvestmentDetailChartSettingsSelect
} from "store/selectors/deal-selectors/deal-selectors";

interface IProps {
  /**
   * "full" (default) renders the charts row plus the data table — used by the
   * Insights look-through full view. "summary" renders the charts only (compact
   * empty state) and is embedded inside the Insights summary section card.
   */
  variant?: "full" | "summary";
  /**
   * Reports the section's loading/empty state to the parent so the Insights
   * summary can hide "Show All" on an empty look-through. Pass a stable callback.
   */
  onSummaryStateChange?: (state: { loading: boolean; isEmpty: boolean }) => void;
  /** Fired after a successful add from the empty state (e.g. to open the full view). */
  onAddSuccess?: () => void;
}

export const LookThrough: React.FC<IProps> = ({ variant = "full", onSummaryStateChange, onAddSuccess }) => {
  const isSummary = variant === "summary";
  const dealId = useDealIdSelector();
  const chartSettings = useDealInvestmentDetailChartSettingsSelect();
  const [refetch, setRefetch] = useState(false);
  const { isMobile } = useDevice();

  const chartDataFormation = Object.entries(chartSettings).map(([key, config]) => ({
    chartName: key,
    groupBy: config.group_by,
    valueBy: config.value_by
  }));

  const { getInvestmentDetailsFieldsAction, getInvestmentDetailsAction, getDispersionChartAction } = useDealActions();

  const { action: getInvestmentDetailsFields, data: columns = [] } = useActionHook(getInvestmentDetailsFieldsAction);
  const {
    action: getTableData,
    data: tableData,
    loading: tableLoading,
    setData: setTableData
  } = useActionHook(getInvestmentDetailsAction);
  const {
    action: getDispersionChart,
    data: dispersionData = [],
    loading: dispersionLoading
  } = useActionHook(getDispersionChartAction);

  useEffect(() => {
    getInvestmentDetailsFields();
  }, []);

  useEffect(() => {
    if (dealId) {
      getTableData(dealId);
      getDispersionChart(dealId);
    }
  }, [dealId]);

  useEffect(() => {
    if (refetch && dealId) {
      getDispersionChart(dealId);
    }
  }, [refetch]);

  const refetchTableData = useCallback(() => {
    if (dealId) {
      getTableData(dealId);
    }
  }, [dealId, getTableData]);

  const hasRows = !!tableData?.rows?.length;
  const isEmpty = !tableLoading && !hasRows;

  useEffect(() => {
    onSummaryStateChange?.({ loading: tableLoading, isEmpty });
  }, [tableLoading, isEmpty, onSummaryStateChange]);

  return (
    <Stack
      sx={{
        p: "1px",
        flexGrow: isSummary ? 0 : 1,
        overflow: "hidden",
        backgroundColor: isSummary ? "transparent" : "#f6f8fc"
      }}
    >
      {hasRows ? (
        <>
          <Stack
            sx={{
              flexDirection: isMobile ? "column" : "row",
              gap: "16px",
              mb: "16px",
              "& > *": {
                flex: 1,
                maxWidth: isMobile ? "100%" : "calc(33.33% - 11px)"
              }
            }}
          >
            {!!dealId &&
              chartDataFormation.map((config, index) => (
                <Chart
                  dealId={dealId}
                  reqConfig={config}
                  columns={columns}
                  key={index}
                  isNeedRefetch={refetch}
                  invalidateRefetch={() => setRefetch(false)}
                />
              ))}
            {!!dealId && <DispersionChart data={dispersionData} loading={dispersionLoading} />}
          </Stack>
          {!isSummary && !!dealId && (
            <Table
              columns={columns}
              setRefetch={() => setRefetch(true)}
              dealId={dealId}
              data={tableData}
              loading={tableLoading}
              setData={setTableData}
              refetchTableData={refetchTableData}
            />
          )}
        </>
      ) : tableLoading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.lookThrough.title}
          description={{
            text: EMPTY_CARD_CONTENT.lookThrough.description,
            externalText: EMPTY_CARD_CONTENT.lookThrough.externalDescription
          }}
          image={{
            src: EmptyCardAssets,
            sx: isSummary ? { width: 180, height: 72 } : { width: 315, height: 125 }
          }}
          fitPage={!isSummary}
          contentWidth={isSummary ? 420 : 560}
          sx={isSummary ? { flexGrow: 0, bgcolor: "transparent" } : undefined}
          actionsElement={
            dealId && (
              <InvestmentDetailActions
                dealId={dealId}
                columns={columns}
                onChartsRefetch={() => setRefetch(true)}
                onTableRefetch={refetchTableData}
                onAddSuccess={onAddSuccess}
              />
            )
          }
        />
      )}
    </Stack>
  );
};
